import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Scale } from "lucide-react";
import { api } from "@/lib/api";
import { legalDocs } from "@/lib/legalDocs";

export default function Legal() {
  const { doc } = useParams();
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/legal").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  const spec = legalDocs[doc];
  if (!spec) return <Navigate to="/legal/terms" replace />;
  const c = {
    business: cfg?.business_name || "Powerball Oracle",
    email: cfg?.support_email,
    law: cfg?.governing_law || "the United States",
    address: cfg?.address,
    refundDays: cfg?.refund_window_days ?? 7,
    trialDays: cfg?.trial_days ?? 3,
    plans: cfg?.plans || [],
    updated: cfg?.updated || "June 2026",
  };
  const sections = spec.sections(c);

  return (
    <div className="min-h-screen bg-ink text-parchment" data-testid={`legal-page-${doc}`}>
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        <Link to="/" data-testid="legal-back-link" className="inline-flex items-center gap-2 label-caps text-parchment/50 hover:text-oracle transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Back to the Oracle
        </Link>
        <div className="mt-10 flex items-center gap-2 label-caps text-oracle"><Scale className="w-3 h-3" strokeWidth={1.5} /> {c.business}</div>
        <h1 data-testid="legal-title" className="font-heading font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-3">{spec.title}</h1>
        <p className="font-body text-xs text-parchment/40 mt-3">Last updated {c.updated}</p>

        <nav className="mt-8 flex flex-wrap gap-2" data-testid="legal-nav">
          {Object.entries(legalDocs).map(([k, v]) => (
            <Link key={k} to={`/legal/${k}`} data-testid={`legal-nav-${k}`}
              className={`px-4 py-1.5 rounded-full border label-caps text-[10px] transition-colors ${k === doc ? "border-oracle text-oracle bg-oracle/10" : "border-white/10 text-parchment/50 hover:text-parchment hover:border-white/30"}`}>
              {v.short}
            </Link>
          ))}
        </nav>

        <article className="mt-12 space-y-10">
          {sections.map((s, i) => (
            <section key={s.h} data-testid={`legal-section-${i}`}>
              <h2 className="font-heading text-lg md:text-2xl text-parchment flex items-baseline gap-3">
                <span className="font-body text-xs text-oracle tabular-nums">{String(i + 1).padStart(2, "0")}</span>{s.h}
              </h2>
              <div className="mt-3 space-y-3 font-body text-sm text-parchment/70 leading-relaxed">
                {s.p.map((t, j) => <p key={j}>{t}</p>)}
              </div>
            </section>
          ))}
        </article>

        <footer className="mt-16 pt-8 border-t border-white/5 font-body text-xs text-parchment/40">
          Questions? {c.email ? <a href={`mailto:${c.email}`} data-testid="legal-contact-email" className="text-oracle hover:underline">{c.email}</a> : <span data-testid="legal-contact-fallback">Reply to any email from {c.business} or your Stripe receipt.</span>}
          {c.address && <span data-testid="legal-address"><span className="mx-2 text-parchment/20">·</span>{c.business}, {c.address}</span>}
          <span className="mx-2 text-parchment/20">·</span>Powerball® is a registered trademark of the Multi-State Lottery Association. {c.business} is not affiliated with or endorsed by MUSL or any state lottery.
        </footer>
      </div>
    </div>
  );
}
